import { Form } from '@carsys/form'
import ConceptConfigurationFormFields from './ConceptConfigurationFormFields'
import { validationSchema } from './validation-schema'
import { notifier } from '@cbgms/base/notifier/use-notifier'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { DBConceptConfigurations } from '@cbgms/api/modules/concept-configuration'
import { IPostConceptConfiguration } from '@cbgms/api/support/conceptConfiguration/apis'
import { useTranslation } from 'react-i18next'
import { useGetConceptConfigurationByConcept } from '../hooks/use-get-concept-configuration'
import { Loader } from '@carsys/ui'
import { useUpdateConceptConfiguration } from '../hooks/use-post-concept-configuration'
import { useDeleteConceptConfiguration } from '../hooks/use-delete-concept-configuration'
import { useGetList } from '../hooks/use-get-list'
import { useInvalidateRows } from '@cbgms/components/list/use-invalidate-rows'
import { useRef } from 'react'

export type SetFieldError<FormValues> = (field: keyof FormValues, message: string | undefined) => void

export interface FormValues {
  AllConceptConfigurations: DBConceptConfigurations[]
  Values: IPostConceptConfiguration
  DeletedConfigurations: DBConceptConfigurations[]
  SelectedConcept: string
}

const ConceptConfigurationForm: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const params = useParams<{ conceptConfigurationUUID: string }>()
  const mutateUpdateConceptConfiguration = useUpdateConceptConfiguration()
  const mutateDeleteConceptConfiguration = useDeleteConceptConfiguration()
  const { data, isLoading, refetch, isRefetching, isFetching } = useGetConceptConfigurationByConcept(params.conceptConfigurationUUID)
  const { data: listData } = useGetList(params.conceptConfigurationUUID)
  const invalidateRows = useInvalidateRows('concept-config-get-list')
  const location = useLocation()
  const isNew = location.pathname.includes('/new')

  const hasNotLoaded = (isLoading || isFetching) && !isRefetching

  const hasError = useRef(false)

  const onSubmit = async (values: FormValues) => {
    await invalidateRows()
    const configurations = values?.Values?.Configurations
    const deletedConfigurations = values?.DeletedConfigurations

    if (
      isNew &&
      (!configurations || (configurations && configurations.length === 0)) &&
      deletedConfigurations &&
      deletedConfigurations.length === 0
    ) {
      notifier.info(t('Concepts.Settings.SelectToProceed'))
      hasError.current = true
      return values
    }
    hasError.current = false
    if (values.Values.Configurations.length > 0) {
      await mutateUpdateConceptConfiguration.mutateAsync({ ...values.Values })
    }
    if (values.DeletedConfigurations.length > 0) {
      values.DeletedConfigurations.map(async configuration => {
        await mutateDeleteConceptConfiguration.mutateAsync(configuration.UUID)
      })
    }
    return values
  }

  const onSubmitSuccess = async () => {
    if (!hasError.current) {
      await invalidateRows()

      notifier.success(t('global.successfully_saved'))
      await refetch()
      history.push('/concept-configuration')
      window.location.reload()
    }
  }

  if (hasNotLoaded) {
    return <Loader loading />
  }

  const initialValues: FormValues = {
    AllConceptConfigurations: data ? data.Configurations : [],
    Values:
      data && data.Configurations.length > 0
        ? {
            Application: data.Configurations[0].Application,
            Configurations: []
          }
        : ({ Application: '' } as IPostConceptConfiguration),
    DeletedConfigurations: [],
    SelectedConcept: params.conceptConfigurationUUID
  }

  return (
    <Form validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} onSubmitSuccess={onSubmitSuccess}>
      <ConceptConfigurationFormFields listData={listData} />
    </Form>
  )
}

export default ConceptConfigurationForm
